/**
 * DocumentScanner – Baustellentauglicher Dokumentenscanner
 * Kamera → Kantenerkennung (OpenCV.js) → Zuschnitt → Bildoptimierung → PDF
 *
 * Workflow: capture → detect → crop → preview → save
 */
import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../../App";
import {
  Camera, RotateCcw, Check, Loader2, Download, Share2,
  FileText, Plus, Trash2, ChevronLeft, Settings2, X, SunMedium,
  ZoomIn, Move, Eye,
} from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

const API = "/api";

/* ═══════════════════════════════════════════════════════
   OpenCV.js Loader (lazy, cached)
   ═══════════════════════════════════════════════════════ */
let cvReady = false;
let cvLoading = false;
const cvCallbacks = [];

function loadOpenCV() {
  return new Promise((resolve, reject) => {
    if (cvReady && window.cv) { resolve(window.cv); return; }
    cvCallbacks.push({ resolve, reject });
    if (cvLoading) return;
    cvLoading = true;

    const script = document.createElement("script");
    script.src = "https://docs.opencv.org/4.9.0/opencv.js";
    script.async = true;
    script.onload = () => {
      // OpenCV.js sets cv as a Module, need to wait for onRuntimeInitialized
      if (window.cv && window.cv.Mat) {
        cvReady = true;
        cvCallbacks.forEach(cb => cb.resolve(window.cv));
        cvCallbacks.length = 0;
      } else {
        // Wait for runtime init
        const check = setInterval(() => {
          if (window.cv && window.cv.Mat) {
            clearInterval(check);
            cvReady = true;
            cvCallbacks.forEach(cb => cb.resolve(window.cv));
            cvCallbacks.length = 0;
          }
        }, 100);
        setTimeout(() => {
          clearInterval(check);
          if (!cvReady) {
            cvCallbacks.forEach(cb => cb.reject(new Error("OpenCV timeout")));
            cvCallbacks.length = 0;
          }
        }, 30000);
      }
    };
    script.onerror = () => {
      cvCallbacks.forEach(cb => cb.reject(new Error("OpenCV load failed")));
      cvCallbacks.length = 0;
      cvLoading = false;
    };
    document.head.appendChild(script);
  });
}

/* ═══════════════════════════════════════════════════════
   Edge Detection
   ═══════════════════════════════════════════════════════ */
function detectDocumentEdges(cv, imgElement) {
  const src = cv.imread(imgElement);
  const gray = new cv.Mat();
  const blurred = new cv.Mat();
  const edges = new cv.Mat();
  const dilated = new cv.Mat();

  cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
  cv.GaussianBlur(gray, blurred, new cv.Size(11, 11), 0);
  cv.Canny(blurred, edges, 50, 150);

  // Dilate to close gaps
  const kernel = cv.Mat.ones(3, 3, cv.CV_8U);
  cv.dilate(edges, dilated, kernel);

  const contours = new cv.MatVector();
  const hierarchy = new cv.Mat();
  cv.findContours(dilated, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);

  let bestContour = null;
  let maxArea = 0;
  const imgArea = src.rows * src.cols;

  for (let i = 0; i < contours.size(); i++) {
    const cnt = contours.get(i);
    const area = cv.contourArea(cnt);
    if (area < imgArea * 0.05) continue; // skip small

    const peri = cv.arcLength(cnt, true);
    const approx = new cv.Mat();
    cv.approxPolyDP(cnt, approx, 0.02 * peri, true);

    if (approx.rows === 4 && area > maxArea) {
      maxArea = area;
      bestContour = [];
      for (let j = 0; j < 4; j++) {
        bestContour.push({
          x: approx.data32S[j * 2],
          y: approx.data32S[j * 2 + 1],
        });
      }
    }
    approx.delete();
  }

  // Cleanup
  src.delete(); gray.delete(); blurred.delete(); edges.delete();
  dilated.delete(); kernel.delete(); contours.delete(); hierarchy.delete();

  if (!bestContour) {
    // Fallback: full image corners
    return null;
  }

  // Order points: top-left, top-right, bottom-right, bottom-left
  return orderPoints(bestContour);
}

function orderPoints(pts) {
  // Sort by sum (x+y): smallest = top-left, largest = bottom-right
  // Sort by diff (x-y): smallest = top-right, largest = bottom-left (inverted)
  const sorted = [...pts];
  sorted.sort((a, b) => (a.x + a.y) - (b.x + b.y));
  const tl = sorted[0];
  const br = sorted[3];
  // Of the middle two, smaller x-y = top-right, larger = bottom-left
  const remaining = [sorted[1], sorted[2]];
  remaining.sort((a, b) => (a.y - a.x) - (b.y - b.x));
  const tr = remaining[0];
  const bl = remaining[1];
  return [tl, tr, br, bl];
}

/* ═══════════════════════════════════════════════════════
   Perspective Transform
   ═══════════════════════════════════════════════════════ */
function perspectiveTransform(cv, imgElement, corners) {
  const src = cv.imread(imgElement);

  const [tl, tr, br, bl] = corners;

  // Calculate output size
  const widthTop = Math.hypot(tr.x - tl.x, tr.y - tl.y);
  const widthBottom = Math.hypot(br.x - bl.x, br.y - bl.y);
  const maxW = Math.round(Math.max(widthTop, widthBottom));

  const heightLeft = Math.hypot(bl.x - tl.x, bl.y - tl.y);
  const heightRight = Math.hypot(br.x - tr.x, br.y - tr.y);
  const maxH = Math.round(Math.max(heightLeft, heightRight));

  const srcPts = cv.matFromArray(4, 1, cv.CV_32FC2, [
    tl.x, tl.y, tr.x, tr.y, br.x, br.y, bl.x, bl.y,
  ]);
  const dstPts = cv.matFromArray(4, 1, cv.CV_32FC2, [
    0, 0, maxW, 0, maxW, maxH, 0, maxH,
  ]);

  const M = cv.getPerspectiveTransform(srcPts, dstPts);
  const dst = new cv.Mat();
  cv.warpPerspective(src, dst, M, new cv.Size(maxW, maxH));

  // Write to canvas and return dataURL
  const canvas = document.createElement("canvas");
  canvas.width = maxW;
  canvas.height = maxH;
  cv.imshow(canvas, dst);

  src.delete(); dst.delete(); M.delete(); srcPts.delete(); dstPts.delete();

  return canvas.toDataURL("image/jpeg", 0.95);
}

/* ═══════════════════════════════════════════════════════
   Image Processing (Color Mode + Enhancement)
   ═══════════════════════════════════════════════════════ */
function processImage(cv, dataUrl, colorMode) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement("canvas");
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0);

      const src = cv.imread(canvas);

      if (colorMode === "bw") {
        const gray = new cv.Mat();
        cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
        const bw = new cv.Mat();
        cv.adaptiveThreshold(gray, bw, 255, cv.ADAPTIVE_THRESH_GAUSSIAN_C, cv.THRESH_BINARY, 21, 10);
        cv.imshow(canvas, bw);
        gray.delete(); bw.delete();
      } else if (colorMode === "gray") {
        const gray = new cv.Mat();
        cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
        // Enhance contrast with CLAHE
        const clahe = new cv.CLAHE(2.0, new cv.Size(8, 8));
        const enhanced = new cv.Mat();
        clahe.apply(gray, enhanced);
        cv.imshow(canvas, enhanced);
        gray.delete(); enhanced.delete(); clahe.delete();
      } else {
        // Color - enhance contrast
        const lab = new cv.Mat();
        cv.cvtColor(src, lab, cv.COLOR_RGBA2RGB);
        const labColor = new cv.Mat();
        cv.cvtColor(lab, labColor, cv.COLOR_RGB2Lab);
        const channels = new cv.MatVector();
        cv.split(labColor, channels);
        const clahe = new cv.CLAHE(1.5, new cv.Size(8, 8));
        const enhanced = new cv.Mat();
        clahe.apply(channels.get(0), enhanced);
        channels.set(0, enhanced);
        cv.merge(channels, labColor);
        cv.cvtColor(labColor, lab, cv.COLOR_Lab2RGB);
        cv.imshow(canvas, lab);
        lab.delete(); labColor.delete(); channels.delete(); clahe.delete(); enhanced.delete();
      }

      src.delete();
      resolve(canvas.toDataURL("image/jpeg", 0.92));
    };
    img.src = dataUrl;
  });
}

/* ═══════════════════════════════════════════════════════
   PDF Generation
   ═══════════════════════════════════════════════════════ */
async function generatePDF(pages, dpi, fileName) {
  const { default: jsPDF } = await import("jspdf");

  // A4 at given DPI
  const a4w = 210; // mm
  const a4h = 297; // mm

  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });

  for (let i = 0; i < pages.length; i++) {
    if (i > 0) doc.addPage();

    const img = new Image();
    await new Promise((resolve) => { img.onload = resolve; img.src = pages[i].processed; });

    const imgRatio = img.width / img.height;
    const pageRatio = a4w / a4h;

    let w, h;
    if (imgRatio > pageRatio) {
      w = a4w;
      h = a4w / imgRatio;
    } else {
      h = a4h;
      w = a4h * imgRatio;
    }

    const x = (a4w - w) / 2;
    const y = (a4h - h) / 2;

    // Determine compression based on DPI
    const quality = dpi <= 150 ? 0.6 : dpi <= 300 ? 0.8 : 0.95;

    doc.addImage(pages[i].processed, "JPEG", x, y, w, h, undefined, "FAST");
  }

  return doc.output("blob");
}

/* ═══════════════════════════════════════════════════════
   Corner Drag Overlay Component
   ═══════════════════════════════════════════════════════ */
function CornerOverlay({ corners, setCorners, imageSize, containerSize }) {
  const svgRef = useRef(null);
  const dragIdx = useRef(null);

  const scaleX = containerSize.w / imageSize.w;
  const scaleY = containerSize.h / imageSize.h;

  const toScreen = (pt) => ({ x: pt.x * scaleX, y: pt.y * scaleY });
  const toImage = (sx, sy) => ({ x: sx / scaleX, y: sy / scaleY });

  const getEventPos = (e) => {
    const rect = svgRef.current.getBoundingClientRect();
    const touch = e.touches ? e.touches[0] : e;
    return { x: touch.clientX - rect.left, y: touch.clientY - rect.top };
  };

  const handleStart = (idx) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    dragIdx.current = idx;
  };

  const handleMove = useCallback((e) => {
    if (dragIdx.current === null) return;
    e.preventDefault();
    const pos = getEventPos(e);
    const sX = containerSize.w / imageSize.w;
    const sY = containerSize.h / imageSize.h;
    const imgPt = {
      x: Math.max(0, Math.min(pos.x, containerSize.w)) / sX,
      y: Math.max(0, Math.min(pos.y, containerSize.h)) / sY,
    };
    setCorners(prev => prev.map((c, i) => i === dragIdx.current ? imgPt : c));
  }, [containerSize, imageSize, setCorners]);

  const handleEnd = useCallback(() => { dragIdx.current = null; }, []);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    el.addEventListener("touchmove", handleMove, { passive: false });
    el.addEventListener("mousemove", handleMove);
    el.addEventListener("touchend", handleEnd);
    el.addEventListener("mouseup", handleEnd);
    el.addEventListener("mouseleave", handleEnd);
    return () => {
      el.removeEventListener("touchmove", handleMove);
      el.removeEventListener("mousemove", handleMove);
      el.removeEventListener("touchend", handleEnd);
      el.removeEventListener("mouseup", handleEnd);
      el.removeEventListener("mouseleave", handleEnd);
    };
  }, [handleMove, handleEnd]);

  const pts = corners.map(toScreen);
  const polygon = pts.map(p => `${p.x},${p.y}`).join(" ");

  return (
    <svg
      ref={svgRef}
      className="absolute inset-0 w-full h-full touch-none"
      viewBox={`0 0 ${containerSize.w} ${containerSize.h}`}
      data-testid="corner-overlay"
    >
      <polygon points={polygon} fill="rgba(59,130,246,0.15)" stroke="#3b82f6" strokeWidth="2" />
      {pts.map((p, i) => (
        <circle
          key={i}
          cx={p.x} cy={p.y} r={18}
          fill="#3b82f6" fillOpacity={0.8} stroke="white" strokeWidth={3}
          onMouseDown={handleStart(i)}
          onTouchStart={handleStart(i)}
          className="cursor-grab active:cursor-grabbing"
          data-testid={`corner-${i}`}
        />
      ))}
    </svg>
  );
}

/* ═══════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════ */
export default function DocumentScanner() {
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const galleryRef = useRef(null);
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  // State
  const [step, setStep] = useState("capture"); // capture | detect | preview | settings
  const [cvStatus, setCvStatus] = useState("idle"); // idle | loading | ready | error
  const [processing, setProcessing] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);
  const [corners, setCorners] = useState([]);
  const [imageSize, setImageSize] = useState({ w: 1, h: 1 });
  const [containerSize, setContainerSize] = useState({ w: 1, h: 1 });
  const [pages, setPages] = useState([]);
  const [settings, setSettings] = useState({ dpi: 300, colorMode: "bw" });
  const [showSettings, setShowSettings] = useState(false);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState(null);
  const [projectName, setProjectName] = useState("");

  // Load user + project for filename
  useEffect(() => {
    authFetch(`${API}/auth/me`).then(r => r.ok ? r.json() : null).then(u => {
      if (u) {
        setUser(u);
        if (u.assigned_project_id) {
          authFetch(`${API}/projects/${u.assigned_project_id}`).then(r => r.ok ? r.json() : null)
            .then(p => { if (p) setProjectName(p.display_name || p.name || ""); }).catch(() => {});
        }
      }
    }).catch(() => {});
  }, []);

  // Load OpenCV lazily - don't block on mount
  const [cvTriggered, setCvTriggered] = useState(false);
  useEffect(() => {
    if (!cvTriggered) return;
    setCvStatus("loading");
    loadOpenCV()
      .then(() => setCvStatus("ready"))
      .catch(() => setCvStatus("error"));
  }, [cvTriggered]);

  // Handle photo capture
  const handleCapture = async (file) => {
    if (!file) return;
    setProcessing(true);

    // Trigger OpenCV loading if not already
    if (!cvTriggered) setCvTriggered(true);

    const reader = new FileReader();
    reader.onload = async (e) => {
      setCapturedImage(e.target.result);
      setStep("detect");

      // Run edge detection
      const img = new Image();
      img.onload = async () => {
        setImageSize({ w: img.width, h: img.height });

        if (cvStatus === "ready" && window.cv) {
          try {
            // Use a hidden canvas to detect
            const canvas = document.createElement("canvas");
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0);

            const detected = detectDocumentEdges(window.cv, canvas);
            if (detected) {
              setCorners(detected);
              toast.success("Dokument erkannt!");
            } else {
              // Fallback: full image with padding
              const pad = 20;
              setCorners([
                { x: pad, y: pad },
                { x: img.width - pad, y: pad },
                { x: img.width - pad, y: img.height - pad },
                { x: pad, y: img.height - pad },
              ]);
              toast.info("Keine Kanten erkannt - bitte manuell anpassen");
            }
          } catch (err) {
            console.error("Edge detection error:", err);
            const pad = 20;
            setCorners([
              { x: pad, y: pad },
              { x: img.width - pad, y: pad },
              { x: img.width - pad, y: img.height - pad },
              { x: pad, y: img.height - pad },
            ]);
          }
        } else {
          // OpenCV not ready, use full image
          setCorners([
            { x: 0, y: 0 },
            { x: img.width, y: 0 },
            { x: img.width, y: img.height },
            { x: 0, y: img.height },
          ]);
        }
        setProcessing(false);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  // Confirm crop and process
  const handleConfirmCrop = async () => {
    if (!capturedImage || corners.length !== 4) return;
    setProcessing(true);

    try {
      const img = new Image();
      await new Promise((resolve) => { img.onload = resolve; img.src = capturedImage; });

      let cropped;
      if (cvStatus === "ready" && window.cv) {
        // Use a canvas for imread
        const canvas = document.createElement("canvas");
        canvas.width = img.width;
        canvas.height = img.height;
        canvas.getContext("2d").drawImage(img, 0, 0);

        cropped = perspectiveTransform(window.cv, canvas, corners);
      } else {
        cropped = capturedImage; // fallback: no transform
      }

      // Apply color mode + enhancement
      let processed = cropped;
      if (cvStatus === "ready" && window.cv) {
        processed = await processImage(window.cv, cropped, settings.colorMode);
      }

      setPages(prev => [...prev, { original: capturedImage, processed, corners: [...corners] }]);
      setCapturedImage(null);
      setCorners([]);
      setStep("preview");
      toast.success("Seite hinzugefuegt!");
    } catch (err) {
      console.error("Processing error:", err);
      toast.error("Verarbeitungsfehler");
    }
    setProcessing(false);
  };

  // Measure container for corner overlay
  useEffect(() => {
    if (step !== "detect" || !containerRef.current) return;
    const measure = () => {
      const el = containerRef.current;
      if (el) {
        setContainerSize({ w: el.clientWidth, h: el.clientHeight });
      }
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [step, capturedImage]);

  // Generate filename
  const getFileName = () => {
    const now = new Date();
    const date = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    const project = projectName ? `_${projectName.replace(/[^a-zA-Z0-9äöüÄÖÜß]/g, "_")}` : "";
    const userName = user?.name ? `_${user.name.split(" ")[0]}` : "";
    return `${date}${project}${userName}.pdf`;
  };

  // Save as PDF
  const handleSavePDF = async () => {
    if (pages.length === 0) return;
    setSaving(true);

    try {
      const blob = await generatePDF(pages, settings.dpi, getFileName());
      const url = URL.createObjectURL(blob);
      const fileName = getFileName();

      // Try Web Share API first (mobile)
      if (navigator.share && navigator.canShare) {
        const file = new File([blob], fileName, { type: "application/pdf" });
        if (navigator.canShare({ files: [file] })) {
          try {
            await navigator.share({ files: [file], title: fileName });
            toast.success("PDF geteilt!");
            setSaving(false);
            return;
          } catch (shareErr) {
            if (shareErr.name !== "AbortError") {
              // Share failed, fall through to download
            }
          }
        }
      }

      // Fallback: download
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen!");
    } catch (err) {
      console.error("PDF error:", err);
      toast.error("PDF-Erstellung fehlgeschlagen");
    }
    setSaving(false);
  };

  // Download only (without share dialog)
  const handleDownloadPDF = async () => {
    if (pages.length === 0) return;
    setSaving(true);
    try {
      const blob = await generatePDF(pages, settings.dpi, getFileName());
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = getFileName();
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF heruntergeladen!");
    } catch {
      toast.error("Fehler beim Erstellen");
    }
    setSaving(false);
  };

  const removePage = (idx) => {
    setPages(prev => prev.filter((_, i) => i !== idx));
  };

  const resetScanner = () => {
    setPages([]);
    setCapturedImage(null);
    setCorners([]);
    setStep("capture");
  };

  const DPI_OPTIONS = [
    { value: 150, label: "150 dpi", desc: "Klein" },
    { value: 300, label: "300 dpi", desc: "Standard" },
    { value: 600, label: "600 dpi", desc: "Hoch" },
  ];

  const COLOR_OPTIONS = [
    { value: "bw", label: "S/W", desc: "Schwarz-Weiss" },
    { value: "gray", label: "Grau", desc: "Graustufen" },
    { value: "color", label: "Farbe", desc: "Originalfarben" },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-[#0f1117] text-white flex flex-col" data-testid="doc-scanner">
      {/* ═══ HEADER ═══ */}
      <div className="flex items-center justify-between px-4 py-3 bg-[#0f1117] border-b border-white/10 flex-shrink-0 safe-area-top">
        <button
          onClick={() => {
            if (pages.length > 0 && step !== "capture") {
              if (window.confirm("Scanner verlassen? Nicht gespeicherte Seiten gehen verloren.")) navigate(-1);
            } else navigate(-1);
          }}
          className="flex items-center gap-1 text-gray-400"
          data-testid="scanner-back"
        >
          <ChevronLeft className="w-5 h-5" />
          <span className="text-sm">Zurueck</span>
        </button>
        <h1 className="text-sm font-bold text-white">Dokument scannen</h1>
        <div className="flex items-center gap-2">
          {cvStatus === "loading" && <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />}
          {cvStatus === "ready" && <Eye className="w-4 h-4 text-green-400" />}
          {cvStatus === "error" && <span className="text-[9px] text-amber-400">Manuell</span>}
          <button onClick={() => setShowSettings(true)} className="p-1.5 rounded-lg active:bg-white/10" data-testid="scanner-settings-btn">
            <Settings2 className="w-5 h-5 text-gray-400" />
          </button>
        </div>
      </div>

      {/* ═══ SETTINGS OVERLAY ═══ */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            className="absolute inset-0 z-50 bg-[#0f1117]/95 flex flex-col"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="scanner-settings-overlay"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <h2 className="text-base font-bold text-white">Einstellungen</h2>
              <button onClick={() => setShowSettings(false)} className="p-2"><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
              {/* DPI */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Aufloesung (DPI)</h3>
                <div className="space-y-2">
                  {DPI_OPTIONS.map(opt => (
                    <button key={opt.value}
                      onClick={() => setSettings(s => ({ ...s, dpi: opt.value }))}
                      className={`w-full h-14 rounded-xl border text-left px-4 flex items-center justify-between transition-all ${settings.dpi === opt.value ? "bg-blue-600/20 border-blue-500/40 text-white" : "bg-white/[0.04] border-white/10 text-gray-300"}`}
                      data-testid={`dpi-${opt.value}`}
                    >
                      <div>
                        <span className="text-sm font-semibold">{opt.label}</span>
                        <span className="text-[10px] text-gray-500 ml-2">{opt.desc}</span>
                      </div>
                      {settings.dpi === opt.value && <Check className="w-5 h-5 text-blue-400" />}
                    </button>
                  ))}
                </div>
              </div>
              {/* Color Mode */}
              <div>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Farbmodus</h3>
                <div className="space-y-2">
                  {COLOR_OPTIONS.map(opt => (
                    <button key={opt.value}
                      onClick={() => setSettings(s => ({ ...s, colorMode: opt.value }))}
                      className={`w-full h-14 rounded-xl border text-left px-4 flex items-center justify-between transition-all ${settings.colorMode === opt.value ? "bg-blue-600/20 border-blue-500/40 text-white" : "bg-white/[0.04] border-white/10 text-gray-300"}`}
                      data-testid={`color-${opt.value}`}
                    >
                      <div>
                        <span className="text-sm font-semibold">{opt.label}</span>
                        <span className="text-[10px] text-gray-500 ml-2">{opt.desc}</span>
                      </div>
                      {settings.colorMode === opt.value && <Check className="w-5 h-5 text-blue-400" />}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ═══ CONTENT ═══ */}
      <div className="flex-1 overflow-y-auto">

        {/* ── STEP: CAPTURE ── */}
        {step === "capture" && (
          <div className="flex flex-col items-center justify-center min-h-[70vh] px-6">
            {/* Camera input (opens camera directly on mobile) */}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="absolute w-0 h-0 overflow-hidden opacity-0 pointer-events-none"
              onChange={(e) => { handleCapture(e.target.files?.[0]); e.target.value = ""; }}
              data-testid="doc-file-input"
            />
            {/* Gallery input (opens photo picker) */}
            <input
              ref={galleryRef}
              type="file"
              accept="image/*"
              className="absolute w-0 h-0 overflow-hidden opacity-0 pointer-events-none"
              onChange={(e) => { handleCapture(e.target.files?.[0]); e.target.value = ""; }}
              data-testid="doc-gallery-input"
            />

            {pages.length > 0 ? (
              <>
                {/* Thumbnail grid of scanned pages */}
                <div className="w-full mb-6">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 text-center">
                    {pages.length} {pages.length === 1 ? "Seite" : "Seiten"} gescannt
                  </h3>
                  <div className="grid grid-cols-3 gap-2">
                    {pages.map((p, i) => (
                      <div key={i} className="relative aspect-[3/4] rounded-lg overflow-hidden border border-white/10 bg-white/[0.04]">
                        <img src={p.processed} alt={`Seite ${i + 1}`} className="w-full h-full object-cover" />
                        <span className="absolute top-1 left-1 bg-black/60 text-[9px] text-white px-1.5 py-0.5 rounded-full">{i + 1}</span>
                        <button onClick={() => removePage(i)}
                          className="absolute top-1 right-1 bg-red-500/80 p-1 rounded-full"
                          data-testid={`remove-page-${i}`}>
                          <Trash2 className="w-3 h-3 text-white" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => { if (fileRef.current) fileRef.current.click(); }}
                  className="w-full h-14 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl text-base font-bold flex items-center justify-center gap-3 active:scale-[0.97] transition-all mb-3"
                  data-testid="doc-add-page-btn"
                >
                  <Plus className="w-6 h-6" /> Weitere Seite
                </button>
                <button
                  onClick={handleSavePDF}
                  disabled={saving}
                  className="w-full h-14 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-2xl text-base font-bold flex items-center justify-center gap-3 active:scale-[0.97] transition-all mb-3"
                  data-testid="doc-save-pdf-btn"
                >
                  {saving ? <Loader2 className="w-6 h-6 animate-spin" /> : <Share2 className="w-6 h-6" />}
                  Speichern als PDF
                </button>
                <button
                  onClick={handleDownloadPDF}
                  disabled={saving}
                  className="w-full h-12 bg-white/[0.06] border border-white/10 text-gray-300 rounded-2xl text-sm font-medium flex items-center justify-center gap-2 active:scale-[0.97] transition-all mb-3"
                  data-testid="doc-download-btn"
                >
                  <Download className="w-5 h-5" /> Herunterladen
                </button>
                <button
                  onClick={() => { if (window.confirm("Alle Seiten verwerfen?")) resetScanner(); }}
                  className="text-sm text-gray-500 py-2"
                  data-testid="doc-reset-btn"
                >
                  Alle verwerfen
                </button>
              </>
            ) : (
              <>
                <div className="w-24 h-24 rounded-full bg-blue-500/10 flex items-center justify-center mb-6">
                  <Camera className="w-12 h-12 text-blue-400" />
                </div>
                <h2 className="text-xl font-bold text-white mb-2">Dokument scannen</h2>
                <p className="text-sm text-gray-500 text-center mb-2 max-w-xs">
                  Fotografieren Sie ein Dokument. Kanten werden automatisch erkannt.
                </p>
                <p className="text-[10px] text-gray-600 mb-8">
                  {settings.colorMode === "bw" ? "S/W" : settings.colorMode === "gray" ? "Graustufen" : "Farbe"} / {settings.dpi} dpi
                </p>
                <button
                  onClick={() => { if (fileRef.current) fileRef.current.click(); }}
                  className="w-full max-w-xs h-16 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl text-lg font-bold flex items-center justify-center gap-3 active:scale-[0.97] transition-all"
                  data-testid="doc-capture-btn"
                >
                  <Camera className="w-7 h-7" /> Foto aufnehmen
                </button>
                <button
                  onClick={() => { if (galleryRef.current) galleryRef.current.click(); }}
                  className="mt-3 text-sm text-blue-400 underline underline-offset-2 py-2"
                  data-testid="doc-gallery-btn"
                >
                  Aus Galerie waehlen
                </button>
                {cvStatus === "loading" && (
                  <p className="mt-4 text-[10px] text-gray-600 flex items-center gap-1">
                    <Loader2 className="w-3 h-3 animate-spin" /> Kantenerkennung wird geladen...
                  </p>
                )}
              </>
            )}
          </div>
        )}

        {/* ── STEP: DETECT (Edge detection + corner adjustment) ── */}
        {step === "detect" && (
          <div className="flex flex-col h-full">
            {processing ? (
              <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <Loader2 className="w-10 h-10 text-blue-400 animate-spin mb-4" />
                <p className="text-sm text-gray-400">Kanten werden erkannt...</p>
              </div>
            ) : (
              <>
                <div className="px-4 py-2">
                  <p className="text-[11px] text-gray-500 text-center flex items-center justify-center gap-1">
                    <Move className="w-3 h-3" /> Ecken ziehen um Zuschnitt anzupassen
                  </p>
                </div>
                <div className="flex-1 px-4 pb-4 flex items-center justify-center">
                  <div ref={containerRef} className="relative w-full max-h-[60vh]" style={{ aspectRatio: imageSize.w / imageSize.h }}>
                    <img
                      ref={imgRef}
                      src={capturedImage}
                      alt="Captured"
                      className="w-full h-full object-contain rounded-lg"
                      onLoad={() => {
                        if (containerRef.current) {
                          setContainerSize({
                            w: containerRef.current.clientWidth,
                            h: containerRef.current.clientHeight,
                          });
                        }
                      }}
                    />
                    {corners.length === 4 && (
                      <CornerOverlay
                        corners={corners}
                        setCorners={setCorners}
                        imageSize={imageSize}
                        containerSize={containerSize}
                      />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* ── STEP: PREVIEW ── */}
        {step === "preview" && pages.length > 0 && (
          <div className="px-4 py-4">
            <div className="flex items-center justify-center mb-4">
              <span className="text-sm text-gray-400 font-medium">Letzte Seite - Vorschau</span>
            </div>
            <div className="aspect-[3/4] w-full max-w-sm mx-auto rounded-xl overflow-hidden border border-white/10 bg-white/[0.04] mb-4">
              <img src={pages[pages.length - 1].processed} alt="Preview" className="w-full h-full object-contain" data-testid="preview-image" />
            </div>
          </div>
        )}
      </div>

      {/* ═══ BOTTOM ACTION BAR ═══ */}
      {step === "detect" && !processing && (
        <div className="px-4 py-3 border-t border-white/10 bg-[#0f1117] flex-shrink-0 safe-area-bottom">
          <div className="flex gap-3">
            <button
              onClick={() => { setCapturedImage(null); setCorners([]); setStep("capture"); }}
              className="flex-1 h-14 bg-white/[0.06] border border-white/10 text-gray-300 rounded-2xl text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.97] transition-all"
              data-testid="doc-retake-btn"
            >
              <RotateCcw className="w-5 h-5" /> Neu aufnehmen
            </button>
            <button
              onClick={handleConfirmCrop}
              className="flex-1 h-14 bg-emerald-600 hover:bg-emerald-500 text-white rounded-2xl text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.97] transition-all"
              data-testid="doc-confirm-crop-btn"
            >
              <Check className="w-5 h-5" /> Zuschneiden
            </button>
          </div>
        </div>
      )}

      {step === "preview" && (
        <div className="px-4 py-3 border-t border-white/10 bg-[#0f1117] flex-shrink-0 safe-area-bottom">
          <div className="flex gap-3">
            <button
              onClick={() => { setStep("capture"); }}
              className="flex-1 h-14 bg-blue-600 hover:bg-blue-500 text-white rounded-2xl text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.97] transition-all"
              data-testid="doc-continue-btn"
            >
              {pages.length > 0 ? (
                <><Plus className="w-5 h-5" /> Weitere Seite</>
              ) : (
                <><Camera className="w-5 h-5" /> Foto aufnehmen</>
              )}
            </button>
            <button
              onClick={handleSavePDF}
              disabled={saving || pages.length === 0}
              className="flex-1 h-14 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white rounded-2xl text-sm font-bold flex items-center justify-center gap-2 active:scale-[0.97] transition-all"
              data-testid="doc-save-btn"
            >
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <FileText className="w-5 h-5" />}
              Als PDF ({pages.length}S.)
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
